/**
 * In-browser screen recording using the MediaRecorder API.
 * Records the current tab/screen for up to maxSeconds.
 */
export class ScreenRecorder {
  private mediaRecorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private stream: MediaStream | null = null;
  private maxSeconds: number;
  private timeout: ReturnType<typeof setTimeout> | null = null;
  private startTime = 0;

  /** Callbacks */
  onStart?: () => void;
  onStop?: (blob: Blob, durationMs: number) => void;
  onError?: (error: Error) => void;
  onTick?: (elapsedSeconds: number) => void;

  private tickInterval: ReturnType<typeof setInterval> | null = null;

  constructor(maxSeconds = 60) {
    this.maxSeconds = maxSeconds;
  }

  get isRecording(): boolean {
    return this.mediaRecorder?.state === 'recording';
  }

  get isSupported(): boolean {
    return (
      typeof navigator !== 'undefined' &&
      typeof navigator.mediaDevices !== 'undefined' &&
      typeof navigator.mediaDevices.getDisplayMedia === 'function' &&
      typeof MediaRecorder !== 'undefined'
    );
  }

  /**
   * Start recording. Prompts the user to select a screen/tab to share.
   */
  async start(): Promise<void> {
    if (!this.isSupported) {
      this.onError?.(new Error('Screen recording is not supported in this browser'));
      return;
    }

    try {
      this.stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: 'browser',
          frameRate: { ideal: 15, max: 30 },
        },
        audio: false,
      });

      // If user cancels the browser picker, stream will have no tracks
      if (!this.stream || this.stream.getTracks().length === 0) {
        this.onError?.(new Error('No screen selected'));
        return;
      }

      // Detect when user clicks "Stop sharing" in the browser UI
      this.stream.getVideoTracks()[0].addEventListener('ended', () => {
        this.stop();
      });

      this.chunks = [];
      const mimeType = this.getSupportedMimeType();

      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType,
        videoBitsPerSecond: 1_500_000, // 1.5 Mbps — good quality, reasonable size
      });

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.chunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        const blob = new Blob(this.chunks, { type: mimeType });
        const duration = Date.now() - this.startTime;
        this.cleanup();
        this.onStop?.(blob, duration);
      };

      this.mediaRecorder.onerror = () => {
        this.cleanup();
        this.onError?.(new Error('Recording failed'));
      };

      this.startTime = Date.now();
      this.mediaRecorder.start(1000); // Collect data every second

      // Auto-stop after maxSeconds
      this.timeout = setTimeout(() => this.stop(), this.maxSeconds * 1000);

      // Tick callback for elapsed time display
      this.tickInterval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - this.startTime) / 1000);
        this.onTick?.(elapsed);
      }, 1000);

      this.onStart?.();
    } catch (err) {
      this.cleanup();
      // User denied permission or browser doesn't support it
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        this.onError?.(new Error('Screen recording permission denied'));
      } else {
        this.onError?.(err instanceof Error ? err : new Error(String(err)));
      }
    }
  }

  /**
   * Stop recording and trigger onStop callback with the video blob.
   */
  stop(): void {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    } else {
      this.cleanup();
    }
  }

  private cleanup(): void {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
    if (this.tickInterval) {
      clearInterval(this.tickInterval);
      this.tickInterval = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
    this.mediaRecorder = null;
  }

  private getSupportedMimeType(): string {
    const types = [
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/webm',
      'video/mp4',
    ];
    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) return type;
    }
    return 'video/webm';
  }
}
